/**
 * Smart Shikka Assistant Live MVP Audit + Release Gate — Registry
 * PR102: Hard Finish Line
 */
function deepFreeze(obj) {
    const propNames = Object.getOwnPropertyNames(obj);
    for (const name of propNames) {
        const value = obj[name];
        if (typeof value === 'object' && value !== null && !Object.isFrozen(value)) {
            deepFreeze(value);
        }
    }
    return Object.freeze(obj);
}
const BLOCKED_ACTIONS = [
    'child_process',
    'old_assistant_alias',
    'secret_literal',
    'shell_command',
    'unapproved_db_write',
    'unapproved_file_write',
    'unapproved_network_call',
    'unapproved_provider_call',
    'unapproved_tool_execution',
    'unknown_provider_call',
    'unknown_runtime_action',
    'unknown_tool_execution',
    'unredacted_secret',
    'unsafe_env_dump',
    'new_foundation_pr',
    'scope_expansion',
    'approval_bypass',
    'runtime_unlock',
    'broad_runtime_unlock',
    'role_mutation',
    'permission_mutation',
];
const COMMON_SAFETY = {
    previewOnly: true,
    readonlyOnly: true,
    requiresOwnerApproval: true,
    requiresUserApproval: true,
    newFoundationPrAllowedInPr102: false,
    scopeExpansionAllowedInPr102: false,
    approvalBypassAllowedInPr102: false,
    runtimeUnlockAllowedInPr102: false,
    broadRuntimeUnlockAllowedInPr102: false,
    roleMutationAllowedInPr102: false,
    permissionMutationAllowedInPr102: false,
    secretLiteralAllowedInPr102: false,
    shellCommandAllowedInPr102: false,
    childProcessAllowedInPr102: false,
    networkCallAllowedInPr102: false,
    providerCallAllowedInPr102: false,
    dbWriteAllowedInPr102: false,
    fileWriteAllowedInPr102: false,
    hiddenScanAllowedInPr102: false,
    unscopedFolderAccessAllowedInPr102: false,
};
const registry = [
    {
        id: 'live_mvp_release_gate_summary',
        displayName: 'Live MVP Release Gate Summary',
        description: 'Audit summary for the Smart Shikka Assistant MVP chain PR84-PR101. Confirms all controlled live paths are properly gated and audited.',
        sourceModules: ['smart-shikka-assistant-live-mvp-release-gate'],
        targetPreviewModules: ['smart-shikka-assistant-orchestrator', 'smart-shikka-assistant-ui-route-preview'],
        relatedRouteIds: ['release-gate-summary'],
        relatedUiPreviewIds: ['mvp-release-dashboard'],
        relatedChatPreviewIds: ['release-status-preview'],
        relatedConversationStatePreviewIds: ['release-conversation-state'],
        relatedApprovalStatePreviewIds: ['release-approval-state'],
        relatedActionRequestPreviewIds: ['release-action-request'],
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ['PR102 is audit/release gate only — no new runtime surface allowed'],
        futurePrScope: ['bugfix-only', 'security-hardening', 'production-hardening'],
        notes: ['Hard finish line for MVP foundation chain'],
        ...COMMON_SAFETY,
    },
    {
        id: 'provider_call_path_audit',
        displayName: 'Provider Call Path Audit',
        description: 'Audits the provider call path from PR93-PR94. Confirms healthcheck, secret-reference, and denial gates are active.',
        sourceModules: ['smart-shikka-assistant-provider-settings-secret-reference', 'smart-shikka-assistant-provider-healthcheck'],
        targetPreviewModules: ['smart-shikka-assistant-provider-settings-secret-reference'],
        relatedRouteIds: ['provider-healthcheck', 'provider-settings'],
        relatedUiPreviewIds: ['provider-status-preview'],
        relatedChatPreviewIds: ['provider-chat-preview'],
        relatedConversationStatePreviewIds: ['provider-conversation-state'],
        relatedApprovalStatePreviewIds: ['provider-approval-state'],
        relatedActionRequestPreviewIds: ['provider-action-request'],
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ['Provider calls require approved healthcheck path only'],
        futurePrScope: ['provider-specific-features'],
        notes: ['Audits PR93-PR94 provider path'],
        ...COMMON_SAFETY,
    },
    {
        id: 'chat_send_path_audit',
        displayName: 'Chat Send Path Audit',
        description: 'Audits the controlled chat send path from PR96. Confirms message sending is gated by approval and redaction.',
        sourceModules: ['smart-shikka-assistant-controlled-chat-send'],
        targetPreviewModules: ['smart-shikka-assistant-readonly-chat-preview'],
        relatedRouteIds: ['chat-send-controlled'],
        relatedUiPreviewIds: ['chat-send-preview'],
        relatedChatPreviewIds: ['chat-send-audit'],
        relatedConversationStatePreviewIds: ['chat-send-conversation-state'],
        relatedApprovalStatePreviewIds: ['chat-send-approval-state'],
        relatedActionRequestPreviewIds: ['chat-send-action-request'],
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ['Chat send requires controlled path with approval gate'],
        futurePrScope: ['chat-feature-expansion'],
        notes: ['Audits PR96 chat send path'],
        ...COMMON_SAFETY,
    },
    {
        id: 'conversation_persistence_path_audit',
        displayName: 'Conversation Persistence Path Audit',
        description: 'Audits the conversation persistence path from PR95. Confirms state persistence is scoped and redacted.',
        sourceModules: ['smart-shikka-assistant-conversation-persistence'],
        targetPreviewModules: ['smart-shikka-assistant-readonly-conversation-state-preview'],
        relatedRouteIds: ['conversation-persistence'],
        relatedUiPreviewIds: ['conversation-state-preview'],
        relatedChatPreviewIds: ['conversation-persistence-chat'],
        relatedConversationStatePreviewIds: ['conversation-persistence-state'],
        relatedApprovalStatePreviewIds: ['conversation-persistence-approval'],
        relatedActionRequestPreviewIds: ['conversation-persistence-action'],
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ['Conversation persistence requires scoped redacted path only'],
        futurePrScope: ['persistence-feature-expansion'],
        notes: ['Audits PR95 persistence path'],
        ...COMMON_SAFETY,
    },
    {
        id: 'approval_runtime_path_audit',
        displayName: 'Approval Runtime Path Audit',
        description: 'Audits the approval runtime gate from PR97. Confirms all tool/action execution requires approval gate pass.',
        sourceModules: ['smart-shikka-assistant-approval-runtime-gate'],
        targetPreviewModules: ['smart-shikka-assistant-readonly-approval-state-preview'],
        relatedRouteIds: ['approval-runtime-gate'],
        relatedUiPreviewIds: ['approval-state-preview'],
        relatedChatPreviewIds: ['approval-runtime-chat'],
        relatedConversationStatePreviewIds: ['approval-runtime-conversation'],
        relatedApprovalStatePreviewIds: ['approval-runtime-state'],
        relatedActionRequestPreviewIds: ['approval-runtime-action'],
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ['All runtime actions require approval gate pass'],
        futurePrScope: ['approval-flow-refinement'],
        notes: ['Audits PR97 approval runtime gate'],
        ...COMMON_SAFETY,
    },
    {
        id: 'tool_execution_path_audit',
        displayName: 'Tool Execution Path Audit',
        description: 'Audits the tool request queue (PR98) and safe internal tool execution (PR99). Confirms deterministic internal-only execution.',
        sourceModules: ['smart-shikka-assistant-tool-request-queue', 'smart-shikka-assistant-safe-internal-tool-execution'],
        targetPreviewModules: ['smart-shikka-assistant-readonly-action-request-preview'],
        relatedRouteIds: ['tool-request-queue', 'safe-tool-execution'],
        relatedUiPreviewIds: ['tool-execution-preview'],
        relatedChatPreviewIds: ['tool-execution-chat'],
        relatedConversationStatePreviewIds: ['tool-execution-conversation'],
        relatedApprovalStatePreviewIds: ['tool-execution-approval'],
        relatedActionRequestPreviewIds: ['tool-execution-action'],
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ['Tool execution limited to deterministic internal-only paths'],
        futurePrScope: ['tool-expansion-with-gates'],
        notes: ['Audits PR98-PR99 tool execution path'],
        ...COMMON_SAFETY,
    },
    {
        id: 'smart_print_readonly_path_audit',
        displayName: 'Smart Print Readonly Path Audit',
        description: 'Audits the Smart Print readonly drive access from PR100. Confirms read-only boundary with user-selected folder scope.',
        sourceModules: ['smart-shikka-assistant-smart-print-readonly-drive-access'],
        targetPreviewModules: ['smart-shikka-assistant-smart-print-readonly-drive-access'],
        relatedRouteIds: ['smart-print-readonly'],
        relatedUiPreviewIds: ['smart-print-readonly-preview'],
        relatedChatPreviewIds: ['smart-print-readonly-chat'],
        relatedConversationStatePreviewIds: ['smart-print-readonly-conversation'],
        relatedApprovalStatePreviewIds: ['smart-print-readonly-approval'],
        relatedActionRequestPreviewIds: ['smart-print-readonly-action'],
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ['Smart Print readonly path — no write/delete/import/export'],
        futurePrScope: ['smart-print-feature-expansion'],
        notes: ['Audits PR100 readonly drive access'],
        ...COMMON_SAFETY,
    },
    {
        id: 'smart_print_write_backup_path_audit',
        displayName: 'Smart Print Write Backup Path Audit',
        description: 'Audits the Smart Print write/backup gate from PR101. Confirms write requires dry-run, backup, dual approval, single-file scope.',
        sourceModules: ['smart-shikka-assistant-smart-print-write-backup-gate'],
        targetPreviewModules: ['smart-shikka-assistant-smart-print-write-backup-gate'],
        relatedRouteIds: ['smart-print-write-backup'],
        relatedUiPreviewIds: ['smart-print-write-preview'],
        relatedChatPreviewIds: ['smart-print-write-chat'],
        relatedConversationStatePreviewIds: ['smart-print-write-conversation'],
        relatedApprovalStatePreviewIds: ['smart-print-write-approval'],
        relatedActionRequestPreviewIds: ['smart-print-write-action'],
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ['Smart Print write requires backup + dry-run + dual approval'],
        futurePrScope: ['smart-print-write-feature-expansion'],
        notes: ['Audits PR101 write/backup gate'],
        ...COMMON_SAFETY,
    },
    {
        id: 'security_regression_matrix',
        displayName: 'Security Regression Matrix',
        description: 'Cross-PR security regression check. Ensures no PR84-PR101 module has regressed on safety invariants.',
        sourceModules: ['smart-shikka-assistant-live-mvp-release-gate'],
        targetPreviewModules: ['smart-shikka-assistant-no-runtime-safety-audit-pack'],
        relatedRouteIds: ['security-regression-matrix'],
        relatedUiPreviewIds: ['security-matrix-preview'],
        relatedChatPreviewIds: ['security-matrix-chat'],
        relatedConversationStatePreviewIds: ['security-matrix-conversation'],
        relatedApprovalStatePreviewIds: ['security-matrix-approval'],
        relatedActionRequestPreviewIds: ['security-matrix-action'],
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ['Security regression detected — release blocked'],
        futurePrScope: ['security-hardening-only'],
        notes: ['Cross-module regression check'],
        ...COMMON_SAFETY,
    },
    {
        id: 'owner_final_release_approval_gate',
        displayName: 'Owner Final Release Approval Gate',
        description: 'Final owner approval required before MVP release. No automated release without explicit owner sign-off.',
        sourceModules: ['smart-shikka-assistant-live-mvp-release-gate'],
        targetPreviewModules: ['smart-shikka-assistant-approval-runtime-gate'],
        relatedRouteIds: ['owner-release-approval'],
        relatedUiPreviewIds: ['owner-approval-preview'],
        relatedChatPreviewIds: ['owner-approval-chat'],
        relatedConversationStatePreviewIds: ['owner-approval-conversation'],
        relatedApprovalStatePreviewIds: ['owner-final-approval'],
        relatedActionRequestPreviewIds: ['owner-approval-action'],
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ['Owner has not signed off on release — gate remains closed'],
        futurePrScope: ['release-approval-refinement'],
        notes: ['No automated release without owner sign-off'],
        ...COMMON_SAFETY,
    },
    {
        id: 'post_pr102_scope_lock',
        displayName: 'Post-PR102 Scope Lock',
        description: 'After PR102, no new foundation PRs. Only bugfix, security, production hardening, and feature-specific PRs allowed.',
        sourceModules: ['smart-shikka-assistant-live-mvp-release-gate'],
        targetPreviewModules: [],
        relatedRouteIds: ['scope-lock'],
        relatedUiPreviewIds: ['scope-lock-preview'],
        relatedChatPreviewIds: ['scope-lock-chat'],
        relatedConversationStatePreviewIds: ['scope-lock-conversation'],
        relatedApprovalStatePreviewIds: ['scope-lock-approval'],
        relatedActionRequestPreviewIds: ['scope-lock-action'],
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ['Foundation chain complete — scope locked after PR102'],
        futurePrScope: ['bugfix', 'security', 'production-hardening', 'feature-specific'],
        notes: ['Hard finish line — no new foundation PRs after PR102'],
        ...COMMON_SAFETY,
    },
    {
        id: 'mvp_complete_handoff',
        displayName: 'MVP Complete Handoff',
        description: 'Handoff record for the completed MVP chain. Documents the audited state for production readiness review.',
        sourceModules: ['smart-shikka-assistant-live-mvp-release-gate'],
        targetPreviewModules: [],
        relatedRouteIds: ['mvp-handoff'],
        relatedUiPreviewIds: ['mvp-handoff-preview'],
        relatedChatPreviewIds: ['mvp-handoff-chat'],
        relatedConversationStatePreviewIds: ['mvp-handoff-conversation'],
        relatedApprovalStatePreviewIds: ['mvp-handoff-approval'],
        relatedActionRequestPreviewIds: ['mvp-handoff-action'],
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ['MVP handoff is documentation-only — no runtime actions'],
        futurePrScope: ['production-deployment'],
        notes: ['Final handoff record for MVP chain'],
        ...COMMON_SAFETY,
    },
];
deepFreeze(registry);
export function getSmartShikkaAssistantLiveMvpReleaseGateEntries() {
    return registry;
}
export function getSmartShikkaAssistantLiveMvpReleaseGateEntryById(id) {
    return registry.find((entry) => entry.id === id);
}
//# sourceMappingURL=smartShikkaAssistantLiveMvpReleaseGateRegistry.js.map